/* eslint-disable react-hooks/exhaustive-deps */
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { MenuItemProps } from "@mui/material/MenuItem";
import { SelectChangeEvent } from "@mui/material/Select";
import { SelectProps } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import { FilterBaseProps } from "../Find.types"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { useEffect, useMemo, useState } from "react"
import { PageType } from "../../../../pages/_app"
import { useLayoutData } from "../../../../utils/contexts/LayoutContext"
import { filterUrl } from "../Find"
import ClearIcon from "@mui/icons-material/Clear"

enum FilterIntervalDropDownName {
  Min = "min",
  Max = "max"
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = (isMobile: boolean | undefined) => {
  return {
    style: {
      zIndex: 11000,
    },
    PaperProps: {
      style: {
        maxHeight: isMobile ? '70%' : ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      },
    },
  }
};

const FilterIntervalDropdown = ({
  filters,
  mmbr,
  pageUrl,
  isMobile
}: FilterBaseProps) => {
  const router = useRouter()
  const { partnerSiteFull, templatePath } = useLayoutData()

  const [minValue, setMinValue] = useState<string | undefined>(filters?.intervalSelectedMin !== undefined ? filters?.intervalSelectedMin.toString() : undefined)
  const [maxValue, setMaxValue] = useState<string | undefined>(filters?.intervalSelectedMax !== undefined ? filters?.intervalSelectedMax.toString() : undefined)

  const path = templatePath === PageType.RBhuPP ? `${templatePath}/templates/${partnerSiteFull?.content?.templateId ?? 'AutoraTemplate'}` : templatePath

  const FindMuiSelect = useMemo(() => dynamic<SelectProps>(() => import(`../../../../design/${path}/_findDesign/Design`).then((style) => style.FindMuiSelect)), [path])
  const FindMuiSelectMenuItem = useMemo(() => dynamic<MenuItemProps>(() => import(`../../../../design/${path}/_findDesign/Design`).then((style) => style.FindMuiSelectMenuItem)), [path])

  useEffect(() => {
    if (filters.intervalSelectedMin !== undefined && filters.intervalSelectedMax !== undefined) {
      const minNum = parseInt(filters.intervalSelectedMin.toString())
      const maxNum = parseInt(filters.intervalSelectedMax.toString())
      if (minNum > maxNum) {
        setMinValue(maxNum.toString())
        setMaxValue(minNum.toString())
        const url = filters.targetUrl?.replace('(min)', maxNum.toString()).replace('(max)', minNum.toString())
        router.push(filterUrl(filters, undefined, url, pageUrl, mmbr), undefined, { scroll: false })
      }
    }
  }, [filters.intervalSelectedMin, filters.intervalSelectedMax])

  const handleChangeMinValue = (e: SelectChangeEvent<unknown>,) => {
    const target = e.target as HTMLInputElement
    const newMinValue = target.value
    setMinValue(newMinValue)

    let newMaxValue = maxValue
    if (newMinValue !== "" && maxValue !== "" && parseInt(newMinValue) > parseInt(maxValue as string)) {
      newMaxValue = newMinValue
      setMaxValue(newMinValue)
    }

    const isEmpty = newMinValue === "" && (newMaxValue === "" || newMaxValue === undefined)

    const url = filters.targetUrl?.replace('(min)', newMinValue ?? '').replace('(max)', newMaxValue ?? '')
    router.push(!isEmpty ? filterUrl(filters, undefined, url, pageUrl, mmbr) : filters.resetUrl as string, undefined, { scroll: false })
  }

  const handleChangeMaxValue = (e: SelectChangeEvent<unknown>,) => {
    const target = e.target as HTMLInputElement
    const newMaxValue = target.value
    setMaxValue(newMaxValue)

    let newMinValue = minValue
    if (newMaxValue !== "" && minValue !== "" && parseInt(newMaxValue) < parseInt(minValue as string)) {
      newMinValue = newMaxValue
      setMinValue(newMaxValue)
    }

    const isEmpty = newMaxValue === "" && (newMinValue === "" || newMinValue === undefined)

    const url = filters.targetUrl?.replace('(min)', newMinValue ?? '').replace('(max)', newMaxValue ?? '')
    router.push(!isEmpty ? filterUrl(filters, undefined, url, pageUrl, mmbr) : filters.resetUrl as string, undefined, { scroll: false })
  }

  const filteredMinItems = useMemo(() => {
    if (!maxValue) return filters.items
    return filters.items?.filter(item => parseInt(item.targetUrlListItem as string) <= parseInt(maxValue))
  }, [filters.items, maxValue])

  const filteredMaxItems = useMemo(() => {
    if (!minValue) return filters.items
    return filters.items?.filter(item => parseInt(item.targetUrlListItem as string) >= parseInt(minValue))
  }, [filters.items, minValue])

  return (
    <Stack direction={"row"} gap={1} width={"100%"} px={isMobile ? 3 : undefined}>
      <FormControl
        fullWidth
      >
        <FindMuiSelect
          size={"small"}
          name={FilterIntervalDropDownName.Min}
          value={minValue ?? filters.intervalSelectedMin?.toString() ??  ""}
          onChange={handleChangeMinValue}
          displayEmpty
          endAdornment={minValue ? (
            <InputAdornment position={"end"} sx={{ mr: 2 }}>
              <IconButton 
                size={"small"}
                edge="end"
                onClick={() => handleChangeMinValue({ target: { name: FilterIntervalDropDownName.Min, value: "" } } as SelectChangeEvent<unknown>)}
              >
                <ClearIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </InputAdornment>
          ) : null}
          MenuProps={MenuProps(isMobile)}
        >
          <FindMuiSelectMenuItem value={""}>-tól</FindMuiSelectMenuItem>
          {filteredMinItems?.map((item, i) => (
            <FindMuiSelectMenuItem key={i} value={item.targetUrlListItem}>{item.title}</FindMuiSelectMenuItem>
          ))}
        </FindMuiSelect>
      </FormControl>
      <FormControl
        fullWidth
      >
        <FindMuiSelect
          size={"small"}
          name={FilterIntervalDropDownName.Max}
          value={maxValue ? maxValue : filters.intervalSelectedMax ? filters.intervalSelectedMax?.toString() : ""}
          onChange={handleChangeMaxValue}
          displayEmpty
          endAdornment={maxValue ? (
            <InputAdornment position={"end"} sx={{ mr: 2 }}>
              <IconButton 
                size={"small"}
                edge="end"
                onClick={() => handleChangeMaxValue({ target: { name: FilterIntervalDropDownName.Max, value: "" } } as SelectChangeEvent<unknown>)}
              >
                <ClearIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </InputAdornment>
          ) : null}
          MenuProps={MenuProps(isMobile)}
        >
          <FindMuiSelectMenuItem value={""}>-ig</FindMuiSelectMenuItem>
          {filteredMaxItems?.map((item, i) => (
            <FindMuiSelectMenuItem key={i} value={item.targetUrlListItem}>{item.title}</FindMuiSelectMenuItem>
          ))}
        </FindMuiSelect>
      </FormControl>
    </Stack>
  )
}

export default FilterIntervalDropdown